import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, RefreshCw, Sparkles, X, XCircle } from 'lucide-react';
import { rootAction, rootApi } from './RootScurityClient';
import './RootUpdateBanner.css';

const POLL_MS = 20000;

async function clearApplicationCache(payload = {}) {
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
  const prefixes = Array.isArray(payload.local_storage_prefixes)
    ? payload.local_storage_prefixes
    : ['ourzest_cache_', 'ourzest_menu_', 'ourzest_root_users_translations'];
  Object.keys(localStorage).forEach((key) => {
    if (prefixes.some((prefix) => key.startsWith(prefix))) localStorage.removeItem(key);
  });
  window.dispatchEvent(new CustomEvent('ourzest:cache-cleared', { detail: payload }));
}

const PRIORITY_META = {
  urgent: { icon: AlertTriangle, label: 'Action required' },
  high: { icon: AlertTriangle, label: 'Action required' },
  medium: { icon: Sparkles, label: 'Update available' },
  low: { icon: Sparkles, label: 'Update available' },
};

/**
 * A single, calm system-alert strip that sits above the top navbar.
 * Renders nothing when there is nothing to say.
 */
export default function RootUpdateBanner({ applicationId, clientVersion = 'web' }) {
  const [updates, setUpdates] = useState([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [dismissedKey, setDismissedKey] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const alive = useRef(true);

  const poll = useCallback(async () => {
    try {
      const query = new URLSearchParams({ action: 'pending_updates' });
      if (applicationId) query.set('application_id', applicationId);
      const result = await rootApi(`root_control_center.php?${query}`);
      if (alive.current) { setUpdates(result.updates || []); setError(''); }
    } catch (e) {
      if (alive.current && e.status !== 401) setError(e.message);
    }
  }, [applicationId]);

  useEffect(() => {
    alive.current = true;
    poll();
    const timer = window.setInterval(poll, POLL_MS);
    return () => { alive.current = false; window.clearInterval(timer); };
  }, [poll]);

  const apply = async (update) => {
    setWorking(true); setError('');
    try {
      const payload = typeof update.payload_json === 'string'
        ? JSON.parse(update.payload_json || '{}') : (update.payload_json || {});
      if (['cache_clear', 'soft_reload', 'hard_reload'].includes(update.update_type)) {
        await clearApplicationCache(payload);
      }
      await rootAction('root_control_center.php', 'acknowledge_update', {
        target_id: update.target_id,
        client_version: clientVersion,
      });
      setUpdates((current) => current.filter((item) => item.target_id !== update.target_id));
      if (update.update_type === 'soft_reload') window.dispatchEvent(new Event('ourzest:data-refresh'));
      if (update.update_type === 'hard_reload') window.location.reload();
    } catch (e) { setError(e.message); }
    finally { setWorking(false); }
  };

  const update = updates[0];
  const bannerKey = error ? 'error' : update?.target_id;
  if ((!updates.length && !error) || dismissedKey === bannerKey) return null;

  const priority = error ? 'urgent' : (update?.priority || 'medium');
  const meta = PRIORITY_META[priority] || PRIORITY_META.medium;
  const Icon = error ? XCircle : meta.icon;
  const extraCount = Math.max(updates.length - 1, 0);

  return (
    <div className={`oz-alert-strip oz-alert-strip--${priority}`} role="status" aria-live="polite">
      <div className="oz-alert-strip__sheen" aria-hidden="true" />
      <div className="oz-alert-strip__inner">
        <div className="oz-alert-strip__badge">
          <Icon size={16} strokeWidth={2.4} />
        </div>

        <div className="oz-alert-strip__copy">
          <span className="oz-alert-strip__eyebrow">{error ? 'System status' : meta.label}</span>
          <span className="oz-alert-strip__title">{error ? 'Update service unavailable' : update.title}</span>
          <span className="oz-alert-strip__message">{error || update.message}</span>
        </div>

        {extraCount > 0 && (
          <button
            type="button"
            className="oz-alert-strip__more"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            +{extraCount} more
            <ChevronDown size={12} className={expanded ? 'is-open' : ''} />
          </button>
        )}

        <div className="oz-alert-strip__actions">
          {update && (
            <button
              type="button"
              className="oz-alert-strip__cta"
              onClick={() => apply(update)}
              disabled={working}
            >
              {working ? <RefreshCw size={14} className="oz-spin" /> : <CheckCircle2 size={14} />}
              {working ? 'Updating…' : 'Update now'}
            </button>
          )}
          <button
            type="button"
            className="oz-alert-strip__dismiss"
            onClick={() => setDismissedKey(bannerKey)}
            title="Dismiss"
            aria-label="Dismiss notification"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {expanded && extraCount > 0 && (
        <div className="oz-alert-strip__queue">
          {updates.slice(1).map((item) => (
            <div key={item.target_id} className="oz-alert-strip__queue-row">
              <span className="oz-alert-strip__queue-dot" />
              <span className="oz-alert-strip__queue-title">{item.title}</span>
              <span className="oz-alert-strip__queue-message">{item.message}</span>
              <button
                type="button"
                className="oz-alert-strip__queue-action"
                onClick={() => apply(item)}
                disabled={working}
              >
                Update
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}